/**

 * Interactive follow-along grader: pitch + optional timing / velocity.

 * Used by class sessions — NEVER auto-demo the lesson for the student.

 *

 * Strict exclusive mode (default): the student may only hold expected keys for

 * the current step. Holding a previous note while pressing the next is a mistake.

 */



import type { MusicPiece, NoteEvent } from "../music/types.js";
import { defaultCPositionFinger } from "../notation/notation.js";



export type HitQuality = "hit" | "wrong" | "early" | "late" | "perfect";



export type FingerId = 1 | 2 | 3 | 4 | 5;



export interface ExpectedStep {

  /** One or more MIDI notes expected together (chord = multiple). */

  midis: number[];

  beat: number;

  duration: number;

  /** Finger per MIDI for this step (teacher-accurate). */

  fingersByMidi: Partial<Record<number, FingerId>>;

  /** @deprecated prefer fingersByMidi — first finger for single notes */

  finger?: FingerId;

  hand?: string;

  /** Mean written velocity for dynamics grading (0–127). */

  targetVelocity?: number;

}



export interface FollowAlongStats {

  expectedCount: number;

  hits: number;

  wrongs: number;

  remaining: number;

  accuracyPct: number;

  completed: boolean;

  perfect: number;

  early: number;

  late: number;

  meanDeltaMs: number;

  jitterMs: number;

  velocityHits: number;

  velocityMisses: number;

  /** Optional MIDI CC64 cue results (classes remain playable without a pedal). */

  pedalHits?: number;

  pedalMisses?: number;

  /** Classic typing-tutor style: notes cleared per minute. */

  notesPerMinute: number;

  elapsedSec: number;

  /** Wrong counts by MIDI note (for heatmap). */

  errorByMidi: Record<string, number>;

}



export interface FollowAlongHit {

  quality: HitQuality;

  expected: ExpectedStep | null;

  playedMidi: number;

  stepIndex: number;

  deltaMs?: number;

  velocityOk?: boolean;

  /** Human-readable coaching tip when quality is wrong. */

  hint?: string;

}



export interface FollowAlongGradeOpts {

  velocity?: number;

  /** AudioContext.currentTime (seconds). */

  audioTime?: number;

  /** Piece BPM — required for beat→time timing grade. */

  bpm?: number;

  /** Session start AudioContext time (when first note expected). */

  timelineOrigin?: number;

  gradeTiming?: boolean;

  gradeVelocity?: boolean;

  /** Compensate output buffer (seconds). */

  latencyCompSec?: number;

  /** Soft dynamics window ± MIDI velocity. */

  velocityWindow?: number;

  /**

   * Currently held MIDI notes (including the note being graded).

   * Required for exclusive / no-extra-key checking.

   */

  heldMidis?: number[];

  /**

   * When true (default), any held key outside the current step's allowed set

   * is a mistake and the step does not advance.

   */

  strictExclusive?: boolean;

}



/** Group simultaneous notes (same beat) into chord steps. */

export function buildExpectedSteps(piece: MusicPiece): ExpectedStep[] {

  const sorted = [...piece.notes].sort(

    (a, b) => a.beat - b.beat || a.midi - b.midi,

  );

  const steps: ExpectedStep[] = [];

  const eps = 1e-6;



  for (const n of sorted) {

    const last = steps[steps.length - 1];

    if (last && Math.abs(last.beat - n.beat) < eps) {

      if (!last.midis.includes(n.midi)) last.midis.push(n.midi);

      last.duration = Math.max(last.duration, n.duration);

      if (n.finger) {

        last.fingersByMidi[n.midi] = n.finger;

        if (!last.finger) last.finger = n.finger;

      }

      if (n.hand && !last.hand) last.hand = n.hand;

      if (n.velocity != null) {

        const prev = last.targetVelocity ?? n.velocity;

        last.targetVelocity = Math.round((prev + n.velocity) / 2);

      }

    } else {

      const fingersByMidi: Partial<Record<number, FingerId>> = {};

      if (n.finger) fingersByMidi[n.midi] = n.finger;

      steps.push({

        midis: [n.midi],

        beat: n.beat,

        duration: n.duration,

        fingersByMidi,

        finger: n.finger,

        hand: n.hand,

        targetVelocity: n.velocity,

      });

    }

  }

  return steps;

}



export class FollowAlongEngine {

  private steps: ExpectedStep[] = [];

  private index = 0;

  private pending = new Set<number>();

  /** Keys already accepted for the current chord step. */

  private satisfied = new Set<number>();

  private hits = 0;

  private wrongs = 0;

  private perfect = 0;

  private early = 0;

  private late = 0;

  private velocityHits = 0;

  private velocityMisses = 0;

  private deltas: number[] = [];

  private errorByMidi: Record<string, number> = {};

  private active = false;

  private pieceId: string | null = null;

  private pieceBpm = 90;

  private wallStartedMs = 0;

  private lastHint: string | null = null;



  get isActive(): boolean {

    return this.active;

  }



  get pieceIdLoaded(): string | null {

    return this.pieceId;

  }



  getLastHint(): string | null {

    return this.lastHint;

  }



  load(piece: MusicPiece): void {

    this.steps = buildExpectedSteps(piece);

    this.index = 0;

    this.hits = 0;

    this.wrongs = 0;

    this.perfect = 0;

    this.early = 0;

    this.late = 0;

    this.velocityHits = 0;

    this.velocityMisses = 0;

    this.deltas = [];

    this.errorByMidi = {};

    this.active = true;

    this.pieceId = piece.id;

    this.pieceBpm = piece.bpm || 90;

    this.wallStartedMs = performance.now();

    this.lastHint = null;

    this.armStep();

  }



  clear(): void {

    this.active = false;

    this.steps = [];

    this.index = 0;

    this.pending.clear();

    this.satisfied.clear();

    this.pieceId = null;

    this.lastHint = null;

  }



  getExpected(): ExpectedStep | null {

    if (!this.active || this.index >= this.steps.length) return null;

    return this.steps[this.index];

  }



  getExpectedMidiList(): number[] {

    return this.getExpected()?.midis ?? [];

  }



  /** Finger numbers for the currently expected midis. */

  getExpectedFingerMap(): Record<number, FingerId> {

    const step = this.getExpected();

    if (!step) return {};

    const out: Record<number, FingerId> = {};

    for (const midi of step.midis) {

      const f =
        step.fingersByMidi[midi] ??
        step.finger ??
        defaultCPositionFinger(midi);

      if (f) out[midi] = f;

    }

    return out;

  }



  getStats(): FollowAlongStats {

    const expectedCount = this.steps.length;

    const completed = this.active && this.index >= this.steps.length;

    const judged = this.hits + this.wrongs;

    const elapsedSec =

      this.wallStartedMs > 0 ? (performance.now() - this.wallStartedMs) / 1000 : 0;

    const n = this.deltas.length;

    const mean = n ? this.deltas.reduce((a, b) => a + b, 0) / n : 0;

    const variance = n

      ? this.deltas.reduce((a, d) => a + (d - mean) ** 2, 0) / n

      : 0;

    const notesPerMinute =

      elapsedSec > 0.5 ? (this.hits / elapsedSec) * 60 : this.hits * 60;

    return {

      expectedCount,

      hits: this.hits,

      wrongs: this.wrongs,

      remaining: Math.max(0, expectedCount - this.index),

      accuracyPct: judged ? (this.hits / judged) * 100 : 100,

      completed,

      perfect: this.perfect,

      early: this.early,

      late: this.late,

      meanDeltaMs: mean,

      jitterMs: Math.sqrt(variance),

      velocityHits: this.velocityHits,

      velocityMisses: this.velocityMisses,

      notesPerMinute,

      elapsedSec,

      errorByMidi: { ...this.errorByMidi },

    };

  }



  /**

   * Grade a user note-on. Advances when the full chord/step is satisfied

   * with no extra keys held (strict exclusive mode).

   */

  handleNoteOn(midiNote: number, opts: FollowAlongGradeOpts = {}): FollowAlongHit {

    if (!this.active || this.index >= this.steps.length) {

      return {

        quality: "wrong",

        expected: null,

        playedMidi: midiNote,

        stepIndex: this.index,

        hint: "No note expected right now.",

      };

    }



    const expected = this.steps[this.index];

    const strict = opts.strictExclusive !== false;

    const held = opts.heldMidis ?? [midiNote];



    if (!this.pending.has(midiNote)) {

      return this.markWrong(

        midiNote,

        expected,

        "Wrong key — press only the glowing key (release extras first).",

      );

    }



    if (strict) {

      const allowed = new Set<number>([...this.pending, ...this.satisfied]);

      const extras = held.filter((m) => !allowed.has(m));

      if (extras.length > 0) {

        return this.markWrong(

          midiNote,

          expected,

          "Extra key held — lift every other finger; only the glowing key(s) may stay down.",

        );

      }

    }



    this.pending.delete(midiNote);

    this.satisfied.add(midiNote);

    this.lastHint = null;



    let timingQuality: HitQuality = "hit";

    let deltaMs: number | undefined;

    if (opts.gradeTiming && opts.audioTime != null && opts.timelineOrigin != null) {

      const bpm = opts.bpm ?? this.pieceBpm;

      const beatSec = 60 / Math.max(1, bpm);

      const expectedTime =

        opts.timelineOrigin + expected.beat * beatSec - (opts.latencyCompSec ?? 0);

      deltaMs = (opts.audioTime - expectedTime) * 1000;

      const abs = Math.abs(deltaMs);

      if (abs <= 40) timingQuality = "perfect";

      else if (abs <= 90) timingQuality = "hit";

      else if (deltaMs < 0) {

        timingQuality = "early";

        this.early += 1;

      } else {

        timingQuality = "late";

        this.late += 1;

      }

      this.deltas.push(deltaMs);

    }



    let velocityOk = true;

    if (

      opts.gradeVelocity &&

      opts.velocity != null &&

      expected.targetVelocity != null

    ) {

      const win = opts.velocityWindow ?? 28;

      velocityOk = Math.abs(opts.velocity - expected.targetVelocity) <= win;

      if (velocityOk) this.velocityHits += 1;

      else this.velocityMisses += 1;

    }



    if (this.pending.size === 0) {

      this.hits += 1;

      if (timingQuality === "perfect") this.perfect += 1;

      this.index += 1;

      this.armStep();

      return {

        quality:

          timingQuality === "perfect"

            ? "perfect"

            : timingQuality === "hit"

              ? "hit"

              : timingQuality,

        expected,

        playedMidi: midiNote,

        stepIndex: this.index - 1,

        deltaMs,

        velocityOk,

      };

    }



    return {

      quality: "hit",

      expected,

      playedMidi: midiNote,

      stepIndex: this.index,

      deltaMs,

      velocityOk,

    };

  }



  private markWrong(

    midiNote: number,

    expected: ExpectedStep,

    hint: string,

  ): FollowAlongHit {

    this.wrongs += 1;

    const key = String(midiNote);

    this.errorByMidi[key] = (this.errorByMidi[key] ?? 0) + 1;

    this.lastHint = hint;

    return {

      quality: "wrong",

      expected,

      playedMidi: midiNote,

      stepIndex: this.index,

      hint,

    };

  }



  private armStep(): void {

    this.pending.clear();

    this.satisfied.clear();

    if (this.index < this.steps.length) {

      for (const m of this.steps[this.index].midis) this.pending.add(m);

    }

  }

}



/** Classic letter grade from accuracy %. */

export function letterGradeFromAccuracy(pct: number): string {

  if (pct >= 97) return "A+";

  if (pct >= 93) return "A";

  if (pct >= 90) return "A-";

  if (pct >= 87) return "B+";

  if (pct >= 83) return "B";

  if (pct >= 80) return "B-";

  if (pct >= 77) return "C+";

  if (pct >= 73) return "C";

  if (pct >= 70) return "C-";

  if (pct >= 60) return "D";

  return "F";

}



export function computeReportMetrics(stats: FollowAlongStats): {

  npm: number;

  letter: string;

  timingNote: string;

  dynamicsNote: string;

  pedalNote: string;

} {

  const npm = Math.round(stats.notesPerMinute);

  const letter = letterGradeFromAccuracy(stats.accuracyPct);

  const timingNote =

    stats.jitterMs > 0

      ? `jitter ${stats.jitterMs.toFixed(0)}ms · bias ${stats.meanDeltaMs >= 0 ? "+" : ""}${stats.meanDeltaMs.toFixed(0)}ms`

      : "timing not scored";

  const dynTotal = stats.velocityHits + stats.velocityMisses;

  const dynamicsNote = dynTotal

    ? `dynamics ${Math.round((stats.velocityHits / dynTotal) * 100)}% in band`

    : "dynamics not scored";

  const pedalTotal = (stats.pedalHits ?? 0) + (stats.pedalMisses ?? 0);

  const pedalNote = pedalTotal

    ? `pedal ${Math.round(((stats.pedalHits ?? 0) / pedalTotal) * 100)}% cue accuracy`

    : "pedal not scored";

  return { npm, letter, timingNote, dynamicsNote, pedalNote };

}



